import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";

import { FieldGroup } from "../../components";
import { WARRANTY_DATE_PLACEHOLDER } from "../../core/warrantyDate";
import { CostFields } from "../../listings/atoms";
import { SupplierWizardModal } from "../../views/Users";
import { currencyOptions } from "../../core/currency";

// Pre-multicurrency wording for the legacy five (EURO/DOLLARS labels).
const LEGACY_CURRENCY_NAMES = { eur: "EURO", usd: "DOLLARS" };
const buyingCurrencyOptions = () =>
  currencyOptions().map((o) => ({
    ...o,
    name: LEGACY_CURRENCY_NAMES[o.id] ?? o.name,
  }));

const ListingClassificationFields = (props) => {
  const disabled = props.disabled || false;
  // When provided, replaces the SUPPLIER picker + NEW SUPPLIER button with the
  // caller's own control (the purpose-built flows' linked Sell Request slot).
  const supplierSlot = props.supplierSlot ?? null;
  const [isCreatingNewSupplier, setIsCreatingNewSupplier] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [value, setValue] = useState(null);
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (props.listing?.supplier) {
      const supplierOption = {
        id: props.listing.supplier_id || props.listing.supplier,
        name: props.listing.supplier, // supplier is already a string (full name)
      };
      setDefaultOptions([supplierOption]);
      setValue(props.listing.supplier_id || props.listing.supplier);
      setFieldValue("supplier", props.listing.supplier);
    }
  }, [props.listing, setFieldValue]);

  const onAfterSubmit = (value) => {
    if (value) {
      const supplierName = value.first_name + " " + value.last_name;
      const newOption = {
        id: value.id,
        name: supplierName,
      };
      setDefaultOptions([newOption]);
      setValue(value.id);
      setFieldValue("supplier", supplierName);
    }
    setIsCreatingNewSupplier(false);
  };

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <FieldGroup
            type="textarea"
            className="short"
            name="private_note"
            placeholder="Internal Comment [will not be displayed]"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-3 pr-md-16">
          <FieldGroup
            name="buying_currency"
            type="select"
            options={buyingCurrencyOptions()}
            placeholder="Buying Currency"
            readOnly
            disabled={disabled}
          />
        </div>
        <div className="col-12 col-md-3 pr-md-16">
          <FieldGroup
            name="buying_price"
            type="numberFormat"
            placeholder="Buying Price"
            disabled={disabled}
          />
        </div>
        {/* Purpose-built flows link the deal to a supplier SELL REQUEST rather
            than picking a bare supplier, so they replace this pair (SUPPLIER
            picker + NEW SUPPLIER) with the linked-request slot. `supplierSlot`
            is only supplied from those flows — every existing caller (the
            legacy listing form, the standalone cart flow) is unchanged. */}
        {supplierSlot ? (
          <div className="col-12">{supplierSlot}</div>
        ) : (
          <>
            <div className="col-12 col-md-3 pr-md-16">
              <FieldGroup
                name="supplier"
                type="userSearchSelect"
                placeholder="Supplier"
                value={value}
                defaultOptions={defaultOptions}
                disabled={disabled}
                onChange={(option) => {
                  if (option) {
                    setFieldValue("supplier", option.name);
                    setValue(option.id);
                    setDefaultOptions([option]);
                  }
                }}
              />
            </div>
            <div className="col-12 col-md-3">
              <button
                type="button"
                disabled={disabled}
                style={{ marginTop: "16px", height: "55px", width: "100%" }}
                onClick={() => setIsCreatingNewSupplier(true)}
              >
                NEW SUPPLIER
              </button>
            </div>
          </>
        )}
      </div>

      <div className="row">
        <div className="col-md-12">
          <FieldGroup
            type="text"
            name="serial_number"
            placeholder="Serial Number"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Below Serial Number. A plain FieldGroup like every other field in this
          section — a bespoke input here is exactly what made it look foreign.
          Text, not a date picker: a warranty is only ever as precise as its
          card, and a picker cannot express "2019". */}
      <div className="row">
        <div className="col-md-12">
          <FieldGroup
            type="text"
            name="warranty_date"
            placeholder={`Warranty Date (${WARRANTY_DATE_PLACEHOLDER})`}
            disabled={disabled}
          />
        </div>
      </div>

      <h4 className="mt-4">Cost:</h4>
      <CostFields disabled={disabled} />

      {props.onNext && (
        <div className="row">
          <div className="col-md-12">
            <div className="action-row">
              <button type="button" onClick={props.onNext}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <SupplierWizardModal
        isOpen={isCreatingNewSupplier}
        onClose={() => setIsCreatingNewSupplier(false)}
        afterSubmit={onAfterSubmit}
      />
    </>
  );
};

export default ListingClassificationFields;
